import { motion, useReducedMotion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

import { cn } from '@/lib/shadcn/utils';
import { ShortlistCandidateDetailSkeleton } from '@/modules/public-shortlist/components/ShortlistCandidateDetailSkeleton';
import { ShortlistCta } from '@/modules/public-shortlist/components/ShortlistCta';
import { ShortlistCandidateDocumentMetadata } from '@/modules/public-shortlist/components/ShortlistDocumentMetadata';
import { ShortlistHeader } from '@/modules/public-shortlist/components/ShortlistHeader';
import { ShortlistInvalidLink } from '@/modules/public-shortlist/components/ShortlistInvalidLink';
import { ShortlistWorkHistory } from '@/modules/public-shortlist/components/ShortlistWorkHistory';
import { SHORTLIST_MAX_WIDTH_CLASS } from '@/modules/public-shortlist/constants';
import { useShortlistRouteParams } from '@/modules/public-shortlist/hooks/useShortlistRouteParams';
import { useShortlistTracking } from '@/modules/public-shortlist/hooks/useShortlistTracking';
import { usePublicJobCandidateDetailQuery } from '@/modules/public-shortlist/queries/usePublicJobCandidateDetailQuery';
import {
  getShortlistRevealInitial,
  getShortlistRevealTransition,
  SHORTLIST_EASE,
  SHORTLIST_LOAD_DELAYS,
  shortlistRevealVisible,
} from '@/modules/public-shortlist/shortlistMotion';
import { Heading } from '@/modules/ui/components/heading/Heading';
import { Paragraph } from '@/modules/ui/components/paragraph/Paragraph';

export function ShortlistCandidateDetail() {
  const { candidateJobId, company, params, roleName, search } =
    useShortlistRouteParams();
  const prefersReducedMotion = useReducedMotion();

  const candidateQuery = usePublicJobCandidateDetailQuery(
    params.isValid && candidateJobId ? candidateJobId : '',
    params.isValid ? params.code : '',
  );

  const { trackBookCallClick } = useShortlistTracking({
    shortlistCode: params.isValid ? params.code : '',
    enabled:
      params.isValid && candidateQuery.isSuccess && !!candidateQuery.data,
    pageType: 'detail',
  });

  if (!params.isValid || !candidateJobId) {
    return <ShortlistInvalidLink />;
  }

  const backHref = {
    pathname: `/${company}/shortlist/${roleName}`,
    search: search ? `?${search}` : undefined,
  };

  if (candidateQuery.isLoading) {
    return <ShortlistCandidateDetailSkeleton />;
  }

  if (candidateQuery.isError || !candidateQuery.data) {
    return (
      <div className="bg-neutral-120 flex min-h-screen flex-col items-center justify-center gap-4 px-4 text-center">
        <Heading variant="h3">We couldn&apos;t find this profile</Heading>
        <Paragraph className="max-w-md text-pretty">
          This candidate may no longer be part of the shortlist.
        </Paragraph>
        <Link
          to={backHref}
          preventScrollReset
          className="text-primary-700 text-sm font-semibold hover:underline"
        >
          Back to shortlist
        </Link>
      </div>
    );
  }

  const candidate = candidateQuery.data;
  const firstName =
    candidate.fullName.trim().split(/\s+/)[0] ?? candidate.fullName;

  return (
    <div className="bg-neutral-120 flex min-h-screen flex-col">
      <ShortlistCandidateDocumentMetadata
        companyName={candidate.company}
        fullName={candidate.fullName}
        jobTitle={candidate.title}
      />
      <ShortlistHeader
        onCtaClick={trackBookCallClick}
        showCta
        contentClassName={SHORTLIST_MAX_WIDTH_CLASS}
      />

      <main className="flex-1">
        <div
          className={cn(
            'mx-auto w-full px-4 py-10 sm:px-6 md:py-14 lg:px-8',
            SHORTLIST_MAX_WIDTH_CLASS,
          )}
        >
          <motion.div
            initial={getShortlistRevealInitial(prefersReducedMotion)}
            animate={shortlistRevealVisible}
            transition={getShortlistRevealTransition(
              prefersReducedMotion,
              SHORTLIST_LOAD_DELAYS.detailBack,
            )}
          >
            <Link
              to={backHref}
              preventScrollReset
              className="text-neutral-650 mb-6 inline-flex items-center gap-1 text-sm font-medium transition-colors hover:text-neutral-900"
            >
              <ArrowLeft className="size-4" aria-hidden />
              Back to shortlist
            </Link>
          </motion.div>

          <motion.header
            initial={getShortlistRevealInitial(prefersReducedMotion)}
            animate={shortlistRevealVisible}
            transition={getShortlistRevealTransition(
              prefersReducedMotion,
              SHORTLIST_LOAD_DELAYS.detailHeader,
            )}
          >
            <Heading variant="h2" className="text-neutral-900">
              {candidate.fullName}
            </Heading>
            <div className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
              <Paragraph
                variant="caption"
                className="text-base text-neutral-600"
              >
                {candidate.title}
                {candidate.company ? (
                  <>
                    {' '}
                    <span className="text-neutral-500">at</span>{' '}
                    {candidate.company}
                  </>
                ) : null}
              </Paragraph>

              {candidate.location ? (
                <>
                  <Paragraph variant="caption" className="text-base">
                    |
                  </Paragraph>
                  <Paragraph
                    variant="caption"
                    className="text-base text-neutral-600"
                  >
                    {candidate.location}
                  </Paragraph>
                </>
              ) : null}
            </div>
          </motion.header>

          {candidate.summary && (
            <motion.section
              className="mt-8 rounded-xl border border-neutral-200 bg-white p-5"
              aria-labelledby="candidate-summary-heading"
              initial={getShortlistRevealInitial(prefersReducedMotion)}
              animate={shortlistRevealVisible}
              transition={getShortlistRevealTransition(
                prefersReducedMotion,
                SHORTLIST_LOAD_DELAYS.detailStrengths,
              )}
            >
              <h2
                id="candidate-summary-heading"
                className="text-lg font-semibold text-neutral-900"
              >
                Profile summary
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-pretty text-neutral-700">
                {candidate.summary}
              </p>
            </motion.section>
          )}

          {candidate.strengths.length > 0 && (
            <motion.section
              className="mt-8"
              aria-labelledby="candidate-strengths-heading"
              initial={getShortlistRevealInitial(prefersReducedMotion)}
              animate={shortlistRevealVisible}
              transition={getShortlistRevealTransition(
                prefersReducedMotion,
                SHORTLIST_LOAD_DELAYS.detailStrengths,
              )}
            >
              <h2
                id="candidate-strengths-heading"
                className="text-lg font-semibold text-neutral-900"
              >
                Why they&apos;re a great fit
              </h2>
              <ul className="mt-4 space-y-3">
                {candidate.strengths.map((strength, index) => (
                  <motion.li
                    key={strength.summary}
                    initial={getShortlistRevealInitial(prefersReducedMotion)}
                    animate={shortlistRevealVisible}
                    transition={
                      prefersReducedMotion
                        ? { duration: 0 }
                        : {
                            duration: 0.4,
                            delay:
                              SHORTLIST_LOAD_DELAYS.detailStrengths +
                              Math.min(index * 0.05, 0.2),
                            ease: SHORTLIST_EASE,
                          }
                    }
                    className="border-primary-600/15 bg-secondary-150/60 rounded-lg border px-4 py-3"
                  >
                    <p className="text-primary-700 text-sm font-semibold">
                      {strength.summary}
                    </p>
                    <p className="mt-1 text-sm leading-relaxed text-pretty text-neutral-800">
                      {strength.explanation}
                    </p>
                  </motion.li>
                ))}
              </ul>
            </motion.section>
          )}

          {candidate.workExperience.length > 0 && (
            <motion.section
              className="mt-8"
              aria-labelledby="candidate-work-history-heading"
              initial={getShortlistRevealInitial(prefersReducedMotion)}
              animate={shortlistRevealVisible}
              transition={getShortlistRevealTransition(
                prefersReducedMotion,
                SHORTLIST_LOAD_DELAYS.detailWorkHistory,
              )}
            >
              <h2
                id="candidate-work-history-heading"
                className="text-lg font-semibold text-neutral-900"
              >
                Experience
              </h2>
              <div className="mt-4">
                <ShortlistWorkHistory
                  workExperience={candidate.workExperience}
                />
              </div>
            </motion.section>
          )}

          <div className="mt-10">
            <ShortlistCta
              onCtaClick={trackBookCallClick}
              title={`Ready to meet ${firstName}?`}
              description="We can coordinate a warm introduction."
            />
          </div>
        </div>
      </main>
    </div>
  );
}
